import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCode,
  faMobileAlt,
  faCreditCard,
  faDatabase,
  faPaintBrush,
  faVial,
  faShieldAlt,
  faBezierCurve,
  faCheckCircle,
  faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import Footer from '../components/Footer';
import './Services.css';

const detailedServices = [
  {
    id: "web-development",
    title: "Web Development",
    tagline: "Scalable Full-Stack Engineering & Microservices",
    icon: faCode,
    description:
      "We design and deploy modern, responsive web applications engineered for speed, high concurrency, and long-term maintainability. Leveraging the MERN stack alongside robust Python and PHP architectures, we build secure systems with fluid UX and zero layout compromises.",
    highlights: [
      "Custom Single Page Applications (SPA) & Progressive Web Apps (PWA)",
      "Secure REST APIs & Node.js/Express and Django backends",
      "Server-side rendering, SEO readiness, and cross-browser testing",
      "Automated CI/CD deployment pipelines on VPS and cloud platforms"
    ],
    image: "https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&q=80&w=800",
    serviceKey: "Web Development"
  },
  {
    id: "mobile-app-development",
    title: "Mobile App Development",
    tagline: "Cross-Platform iOS & Android Applications",
    icon: faMobileAlt,
    description:
      "Transforming business workflows into intuitive, high-performance mobile experiences. We build lightweight cross-platform apps using React Native and hybrid tools that deliver native feel, fluid touch gestures, and offline data sync.",
    highlights: [
      "Cross-platform parity across iOS and Android from a single codebase",
      "Offline-first architecture and real-time push notifications",
      "Seamless backend synchronization and biometric authentication",
      "App Store & Google Play Store release management"
    ],
    image: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&q=80&w=800",
    serviceKey: "Mobile App Development"
  },
  {
    id: "payment-integration",
    title: "Payment Integration",
    tagline: "Instant M-Pesa STK Push & Automated Reconciliations",
    icon: faCreditCard,
    description:
      "Specialized in the Kenyan digital commerce ecosystem. We engineer secure financial bridges using Safaricom's Daraja 2.0 API, supporting automated customer STK Push prompts, B2C disbursements, and robust transaction webhook callbacks.",
    highlights: [
      "Instant M-Pesa Express (STK Push) customer checkout prompts",
      "Real-time IPN / Callback validation and duplicate transaction shielding",
      "Automated ledger updates and financial reporting hooks",
      "Card payment gateway bridges (Stripe, Flutterwave, Pesapal)"
    ],
    image: "https://images.unsplash.com/photo-1559526324-4b87b5e36e44?auto=format&fit=crop&q=80&w=800",
    serviceKey: "Payment Integration"
  },
  {
    id: "database-optimization",
    title: "Database Optimization",
    tagline: "High-Throughput Relational & Document Data Architecture",
    icon: faDatabase,
    description:
      "A software system is only as performant as its data layer. We design, restructure, and optimize schema architectures across MongoDB, MySQL, and PostgreSQL to eliminate query bottlenecks and prevent resource exhaustion under heavy traffic.",
    highlights: [
      "Schema normalization, indexing strategies, and aggregation tuning",
      "Database migrations and zero-downtime data restructuring",
      "Connection pooling, caching layers (Redis), and read replication",
      "Automated backups and point-in-time disaster recovery configurations"
    ],
    image: "https://images.unsplash.com/photo-1544383835-bda2bc66a55d?auto=format&fit=crop&q=80&w=800",
    serviceKey: "Database Optimization"
  },
  {
    id: "website-designing",
    title: "Website Designing",
    tagline: "Conversion-Focused UI/UX Systems & Responsive Prototypes",
    icon: faPaintBrush,
    description:
      "Modern websites demand more than clean colors. We craft interactive, visually striking design systems with strong typographic hierarchy, high-contrast usability, and brand consistency that captivate visitors from the first scroll.",
    highlights: [
      "High-fidelity interactive prototypes & component design libraries",
      "Fluid, responsive grid systems tailored for both desktop and mobile viewports",
      "Custom brand aesthetics, dark/light theme switching, and micro-animations",
      "Accessibility compliance (WCAG) and UX heuristic optimization"
    ],
    image: "https://images.unsplash.com/photo-1507238691740-187a5b1d37b8?auto=format&fit=crop&q=80&w=800",
    serviceKey: "Website Designing"
  },
  {
    id: "software-testing",
    title: "Software Testing & Consultancy",
    tagline: "Quality Assurance, Technical Audits & Architecture Reviews",
    icon: faVial,
    description:
      "Protect your engineering investments before launch. We run structured functional, integration, load, and automated test suites to ensure edge-case resiliency, system predictability, and top-tier code maintainability.",
    highlights: [
      "End-to-end integration and unit testing suites",
      "API payload validation, load handling, and stress benchmarking",
      "Third-party codebase and architecture feasibility audits",
      "Infrastructure scaling recommendations and cost optimization"
    ],
    image: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&q=80&w=800",
    serviceKey: "Software Testing and Consultancy"
  },
  {
    id: "penetration-testing",
    title: "Penetration Testing & Security",
    tagline: "Threat Modeling, Vulnerability Assessments & Hardening",
    icon: faShieldAlt,
    description:
      "Proactively uncover vulnerabilities before malicious actors do. Our security assessments inspect web APIs, server configurations, and authentication pipelines for OWASP Top 10 vulnerabilities, unauthorized access risks, and privilege escalation bugs.",
    highlights: [
      "Black-box and white-box web application vulnerability audits",
      "JWT and session management security evaluations",
      "Server port audits, Nginx header hardening, and reverse-proxy firewalls",
      "Detailed actionable remediation reports for dev teams"
    ],
    image: "https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?auto=format&fit=crop&q=80&w=800",
    serviceKey: "Penetration Testing"
  },
  {
    id: "graphic-designing",
    title: "Graphic Designing",
    tagline: "Corporate Identity, Brand Assets & Visual Marketing Media",
    icon: faBezierCurve, 
    description:
      "Deliver your brand's technical prowess with unforgettable visual storytelling. We produce crisp vector brand assets, product mockups, investor deck collateral, and marketing visuals that distinguish your digital presence.",
    highlights: [
      "Scalable vector logos, branding kits, and brand typography guides",
      "Social media visual marketing assets and banner campaigns",
      "Software product mockups and conceptual UI showcase banners",
      "Print-ready technical brochures and commercial collateral"
    ],
    image: "https://images.unsplash.com/photo-1626785774573-4b799315345d?auto=format&fit=crop&q=80&w=800",
    serviceKey: "Graphic Designing"
  }
];

const Services = () => {
  const navigate = useNavigate();

  const handleInquire = (serviceKey) => {
    navigate('/contact', { state: { selectedService: serviceKey } });
  };

  return (
    <div className="services-page-wrapper">
      {/* Ambient Large Glass Watermark Background */}
      <div className="services-watermark-bg" aria-hidden="true">
        <img src="/logo.png" alt="" className="watermark-logo" />
      </div>

      <div className="container services-page-content">
        {/* Header Intro */}
        <header className="services-hero-header">
          <span className="services-badge">End-to-End Solutions</span>
          <h1>Engineering Capabilities & <span>Services</span></h1>
          <p>
            From high-throughput cloud web platforms and M-Pesa automated ledgers to 
            thorough security audits, explore how Stemiot Softwares powers modern digital ecosystems.
          </p>
        </header>

        {/* Alternating Service Rows */}
        <div className="services-list-container">
          {detailedServices.map((service, index) => (
            <article 
              key={service.id} 
              id={service.id} 
              className="service-row-card"
            >
              <div className="service-content-col">
                <div className="service-pill">
                  <FontAwesomeIcon icon={service.icon} className="pill-icon" />
                  <span>{String(index + 1).padStart(2, '0')}</span>
                </div>
                <h2>{service.title}</h2>
                <h4 className="service-tagline">{service.tagline}</h4>
                <p className="service-desc">{service.description}</p>

                <ul className="service-highlights-list">
                  {service.highlights.map((highlight, hIdx) => (
                    <li key={hIdx}>
                      <FontAwesomeIcon icon={faCheckCircle} className="check-icon" />
                      <span>{highlight}</span>
                    </li>
                  ))}
                </ul>

                <button 
                  className="btn-service-inquire"
                  onClick={() => handleInquire(service.serviceKey)}
                >
                  Inquire About {service.title} <FontAwesomeIcon icon={faArrowRight} />
                </button>
              </div>

              <div className="service-image-col">
                <div className="image-frame">
                  <img src={service.image} alt={service.title} loading="lazy" />
                  <div className="image-glass-glare"></div>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Global Bottom Consultation CTA */}
        <section className="services-bottom-cta">
          <h2>Have a specialized software project in mind?</h2>
          <p>Let's collaborate to build an agile, scalable product configured around your technical needs.</p>
          <Link to="/contact" className="btn-primary">
            Schedule a Consultation
          </Link>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default Services;